import { useState, useRef } from 'react';
import { importPapers, importPapersPDF } from '../api';

const TABS = [
  { id: 'doi', label: 'DOI List' },
  { id: 'bibtex', label: 'BibTeX / RIS' },
  { id: 'pdf', label: 'PDF Upload' },
];

export default function BulkImportModal({ onImport, onClose }) {
  const [activeTab, setActiveTab] = useState('doi');
  const [doiText, setDoiText] = useState('');
  const [bibtexText, setBibtexText] = useState('');
  const [pdfFile, setPdfFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const hasInput =
    activeTab === 'doi' ? doiText.trim().length > 0 :
    activeTab === 'bibtex' ? bibtexText.trim().length > 0 :
    pdfFile !== null;

  const parseDois = (text) =>
    text
      .split(/[\n,]+/)
      .map((s) => s.trim())
      .filter(Boolean);

  const estimatedCount =
    activeTab === 'doi' ? parseDois(doiText).length :
    activeTab === 'bibtex' ? (bibtexText.trim().length > 0 ? '?' : 0) :
    pdfFile ? '?' : 0;

  const handleImport = async () => {
    setLoading(true);
    setResult(null);
    try {
      let response;
      if (activeTab === 'pdf') {
        response = await importPapersPDF(pdfFile);
      } else {
        const dois = activeTab === 'doi' ? parseDois(doiText) : [];
        const bibtex = activeTab === 'bibtex' ? bibtexText.trim() : '';
        const ris = '';
        response = await importPapers(dois, bibtex, ris);
      }
      const data = response.data;
      setResult({
        papers: data.papers || [],
        failed: data.failed || [],
      });
      if (data.papers && data.papers.length > 0) {
        onImport(data.papers);
      }
    } catch (err) {
      setResult({
        papers: [],
        failed: [],
        error: err.response?.data?.detail || 'Import failed. Please check your input and try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-xl w-full max-w-lg mx-4 overflow-hidden"
        style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px, rgba(0,0,0,0.04) 0px 8px 24px' }}
      >
        {/* Modal header */}
        <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: '1px solid #ebebeb' }}>
          <h2 className="text-sm font-semibold text-[#171717]" style={{ letterSpacing: '-0.32px' }}>Bulk Import Papers</h2>
          <button
            onClick={onClose}
            className="text-[#808080] hover:text-[#171717] transition-colors text-xl leading-none"
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        {/* Tabs */}
        <div className="flex" style={{ borderBottom: '1px solid #ebebeb' }}>
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => { setActiveTab(tab.id); setResult(null); }}
              className="flex-1 px-4 py-2.5 text-sm font-medium transition-colors"
              style={{
                color: activeTab === tab.id ? '#171717' : '#808080',
                borderBottom: activeTab === tab.id ? '2px solid #171717' : '2px solid transparent',
                background: activeTab === tab.id ? '#fafafa' : 'transparent',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="px-5 py-4">
          {activeTab === 'doi' && (
            <div>
              <label className="block text-sm font-medium text-[#171717] mb-1.5" style={{ letterSpacing: '-0.32px' }}>
                Paste DOIs — one per line or comma-separated
              </label>
              <textarea
                value={doiText}
                onChange={(e) => { setDoiText(e.target.value); setResult(null); }}
                placeholder={`10.1038/nature12373\n10.1126/science.1234567\n10.1016/j.cell.2020.01.001`}
                rows={7}
                className="w-full rounded-lg px-3 py-2 text-sm font-mono text-[#171717] placeholder-[#808080] bg-white focus:outline-none focus:ring-2 focus:ring-[#0a72ef] resize-y"
                style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
              />
              <p className="mt-1 text-xs text-[#808080]">
                {parseDois(doiText).length > 0
                  ? `${parseDois(doiText).length} DOI${parseDois(doiText).length !== 1 ? 's' : ''} detected`
                  : 'Enter at least one DOI to import'}
              </p>
            </div>
          )}

          {activeTab === 'bibtex' && (
            <div>
              <label className="block text-sm font-medium text-[#171717] mb-1.5" style={{ letterSpacing: '-0.32px' }}>
                Paste BibTeX or RIS file contents
              </label>
              <textarea
                value={bibtexText}
                onChange={(e) => { setBibtexText(e.target.value); setResult(null); }}
                placeholder={`@article{smith2023,\n  title={Example Paper},\n  author={Smith, John},\n  journal={Nature},\n  year={2023}\n}`}
                rows={8}
                className="w-full rounded-lg px-3 py-2 text-sm font-mono text-[#171717] placeholder-[#808080] bg-white focus:outline-none focus:ring-2 focus:ring-[#0a72ef] resize-y"
                style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
              />
              <p className="mt-1 text-xs text-[#808080]">
                Supports .bib (BibTeX) and .ris (RIS) formats — paste the raw file content above
              </p>
            </div>
          )}

          {activeTab === 'pdf' && (
            <div>
              <label className="block text-sm font-medium text-[#171717] mb-1.5" style={{ letterSpacing: '-0.32px' }}>
                Upload a PDF
              </label>
              <div
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  const f = e.dataTransfer.files[0];
                  if (f && f.type === 'application/pdf') { setPdfFile(f); setResult(null); }
                }}
                className="border-2 border-dashed rounded-lg px-4 py-8 text-center cursor-pointer transition-colors"
                style={{ borderColor: '#ebebeb' }}
              >
                {pdfFile ? (
                  <div>
                    <p className="text-sm font-medium text-[#171717]">{pdfFile.name}</p>
                    <p className="text-xs text-[#808080] mt-1">{(pdfFile.size / 1024).toFixed(0)} KB — click to change</p>
                  </div>
                ) : (
                  <div>
                    <svg className="mx-auto h-10 w-10 text-[#ebebeb] mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                    </svg>
                    <p className="text-sm text-[#4d4d4d]">Drop a PDF here or <span className="text-[#171717] font-medium">click to browse</span></p>
                    <p className="text-xs text-[#808080] mt-1">Extracts DOIs and references from the document (max 20 MB)</p>
                  </div>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,application/pdf"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) { setPdfFile(f); setResult(null); }
                }}
              />
            </div>
          )}

          {result && !result.error && (
            <div className="mt-3 rounded-lg px-4 py-3 text-sm space-y-1.5" style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}>
              <p className="font-medium text-[#171717]">
                Import complete —
                <span className="text-green-700 ml-1">
                  {result.papers.length} paper{result.papers.length !== 1 ? 's' : ''} added
                </span>
                {result.failed.length > 0 && (
                  <span className="text-red-600 ml-1">
                    , {result.failed.length} failed
                  </span>
                )}
              </p>
              {result.failed.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-red-600 mb-1">Failed identifiers:</p>
                  <ul className="space-y-0.5">
                    {result.failed.map((doi, i) => (
                      <li key={i} className="text-xs text-red-500 font-mono bg-red-50 rounded px-2 py-0.5">
                        {doi}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {result?.error && (
            <div className="mt-3 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
              {result.error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-4" style={{ borderTop: '1px solid #ebebeb' }}>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-[#4d4d4d] hover:text-[#171717] transition-colors"
          >
            {result && !result.error ? 'Done' : 'Cancel'}
          </button>

          {!result && (
            <button
              onClick={handleImport}
              disabled={!hasInput || loading}
              className="px-5 py-2 bg-[#171717] text-white text-sm font-medium rounded-md hover:bg-[#2e2e2e] disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Importing...
                </>
              ) : (
                `Import${estimatedCount !== 0 && estimatedCount !== '?' ? ` ${estimatedCount} paper${estimatedCount !== 1 ? 's' : ''}` : ''}`
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
